import type { ElementNode } from '../store/editor.store'

/**
 * Una sección nueva alterna con la de arriba: si ésa es «Secondary section»,
 * la nueva es «Default section»; si no, «Secondary». Así una página que se
 * arma sección a sección queda Principal · Secundaria · Principal… sin que
 * nadie tenga que acordarse, y dos secciones seguidas nunca se funden en un
 * solo bloque del mismo color.
 *
 * Sólo decide por las secciones que dejan el fondo a su estilo. Una con fondo
 * propio —un hero oscuro, una foto de fondo— es una decisión de diseño y no se
 * toca; si es la de arriba, la nueva va en «Default», que contrasta con casi
 * cualquier fondo marcado.
 */

const DEFAULT = 'section-default'
const SECONDARY = 'section-secondary'
const BACKGROUND_KEYS = ['background', 'backgroundColor', 'backgroundImage']

function isChrome(node: ElementNode): boolean {
  return node.behavior?.type === 'nav'
    || node.sectionType?.startsWith('header@') === true
    || node.sectionType?.startsWith('footer@') === true
}

/** Si el fondo lo pone la sección misma y no su estilo. */
function hasOwnBackground(node: ElementNode): boolean {
  const refs = node.styleRefs
  if (refs?.colors?.background || refs?.colors?.backgroundColor) return true
  if (refs?.overrides?.some((key) => BACKGROUND_KEYS.includes(key))) return true
  return Object.values(node.styles ?? {}).some((styles) => (
    styles && BACKGROUND_KEYS.some((key) => {
      const value = (styles as Record<string, unknown>)[key]
      return typeof value === 'string' && value.trim() !== '' && value !== 'transparent' && value !== 'none'
    })
  ))
}

/** Si la sección sigue a su estilo de sección y se puede alternar. */
function followsSectionStyle(node: ElementNode): boolean {
  if (node.type !== 'section' || isChrome(node) || hasOwnBackground(node)) return false
  const token = node.styleRefs?.section
  return !token || token === DEFAULT || token === SECONDARY
}

/**
 * La página con la sección `id` ya alternada respecto a la de arriba. Si no
 * está en la raíz, o no sigue a su estilo, la página vuelve igual. La primera
 * sección de la página se queda como venga.
 */
export function alternateNewSection(elements: ElementNode[], id: string): ElementNode[] {
  const index = elements.findIndex((node) => node.id === id)
  const node = elements[index]
  if (!node || !followsSectionStyle(node)) return elements

  let above: ElementNode | undefined
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    const candidate = elements[cursor]!
    if (candidate.type === 'section' && !isChrome(candidate)) { above = candidate; break }
  }
  if (!above) return elements

  const aboveToken = followsSectionStyle(above) ? (above.styleRefs?.section ?? DEFAULT) : null
  const token = aboveToken === DEFAULT ? SECONDARY : DEFAULT
  if ((node.styleRefs?.section ?? DEFAULT) === token && node.styleRefs?.section) return elements

  const next = elements.slice()
  next[index] = { ...node, themeRole: node.themeRole ?? 'section', styleRefs: { ...(node.styleRefs ?? {}), section: token } }
  return next
}
